import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiDroplet, FiPlus, FiEdit2, FiTrash2, FiSearch, FiCheck,
  FiAlertTriangle, FiCoffee, FiX, FiClock, FiList, FiCalendar,
  FiRefreshCw, FiPackage, FiDollarSign, FiCheckCircle, FiXCircle
} from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';
const ITEMS_PER_PAGE = 20;

// Helper function to remove Vietnamese diacritics for search
const removeVietnameseDiacritics = (str) => {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
};

const IngredientsListView = ({ onAdd, onEdit, refreshKey }) => {
  const location = useLocation();
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Pagination states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const observerTarget = useRef(null);
  const searchDebounceRef = useRef(null);
  const isFirstRender = useRef(true);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('list'); // 'list' or 'logs'
  
  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [longPressTimer, setLongPressTimer] = useState(null);
  
  // Delete confirm
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Brewing modal
  const [showBrewingModal, setShowBrewingModal] = useState(false);
  const [brewingRecipes, setBrewingRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [brewingForm, setBrewingForm] = useState({
    batches: 1,
    autoDiscardEndOfDay: false,
    note: ''
  });
  const [brewingLoading, setBrewingLoading] = useState(false);

  // Brewing logs state
  const [brewingLogs, setBrewingLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFilters, setLogsFilters] = useState({
    startDate: '',
    endDate: '',
    status: ''
  });
  const [deleteBrewingLogConfirm, setDeleteBrewingLogConfirm] = useState(null); // { id, name, restoreIngredients }
  const [selectedBrewingLogs, setSelectedBrewingLogs] = useState([]); // For bulk delete

  // Filter for ingredients list
  const [ingredientFilter, setIngredientFilter] = useState('all'); // 'all' | 'imported' | 'brewed' | 'lowStock'
  const [brewedIngredientIds, setBrewedIngredientIds] = useState([]);

  const packagingLabels = { box: 'Hộp', can: 'Lon', bottle: 'Chai', bag: 'Túi', piece: 'Quả', item: 'Cái', kg: 'Kg', unit: 'Đv' };
  const measurementLabels = { kg: 'Kg', g: 'Gram', liter: 'Lít', ml: 'ml', piece: 'Cái' };
  
  // Check if ingredient is a brewing result (from brewing recipe)
  const isBrewingResult = (ingredient) => {
    return ingredient.fromRecipe || ingredient.notes?.includes('công thức ủ');
  };
  
  // Get display unit for ingredient (measurementUnit for brewing results, packagingUnit for others)
  const getDisplayUnit = (ingredient) => {
    if (isBrewingResult(ingredient)) {
      return measurementLabels[ingredient.measurementUnit] || ingredient.measurementUnit;
    }
    return packagingLabels[ingredient.packagingUnit] || ingredient.packagingUnit;
  };

  // Debounce search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [search]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load ingredients with pagination
  const loadIngredients = useCallback(async (pageNum = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      // Special case: lowStock filter uses different API
      if (ingredientFilter === 'lowStock') {
        const response = await axios.get(`${API_URL}/ingredients/alerts/low-stock`);
        if (reset || pageNum === 1) {
          setIngredients(response.data.data || []);
        }
        setPage(1);
        setHasMore(false); // No pagination for low stock
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      const params = {
        page: pageNum,
        limit: ITEMS_PER_PAGE,
        skipCount: 'true'
      };

      if (debouncedSearch) params.search = debouncedSearch;
      
      // Map ingredientFilter to API params
      if (ingredientFilter === 'brewed') {
        // Nguyên liệu ủ (được tạo tự động từ công thức ủ)
        params.fromRecipe = 'true';
      } else if (ingredientFilter === 'imported') {
        // Nguyên liệu nhập (do admin tạo thủ công, không phải từ công thức ủ)
        params.fromRecipe = 'false';
      }

      const response = await axios.get(`${API_URL}/ingredients`, { params });
      const { data, pagination } = response.data;

      if (reset || pageNum === 1) {
        setIngredients(data || []);
      } else {
        setIngredients(prev => [...prev, ...(data || [])]);
      }

      setPage(pageNum);
      setHasMore(pagination?.hasMore || false);
    } catch (error) {
      toast.error('Không thể tải danh sách nguyên liệu');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearch, ingredientFilter]);

  // Load more items
  const loadMoreItems = useCallback(() => {
    if (!hasMore || loadingMore) return;
    loadIngredients(page + 1, false);
  }, [hasMore, loadingMore, page, loadIngredients]);

  // Check location state for lowStock filter (from notification banner)
  useEffect(() => {
    if (location.state?.filter === 'lowStock') {
      setIngredientFilter('lowStock');
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Initial load on mount and refreshKey change
  useEffect(() => {
    loadIngredients(1, true);
    loadBrewedIngredientIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Reload when search or filter changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    loadIngredients(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, ingredientFilter]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ingredientFilter]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const target = observerTarget.current;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMoreItems();
        }
      },
      { threshold: 0.1 }
    );

    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [hasMore, loadingMore, loading, loadMoreItems]);

  // Handle openBrewingModal from navigation state (from Dashboard quick action)
  useEffect(() => {
    if (location.state?.openBrewingModal) {
      openBrewingModal();
      // Clear the state to prevent re-opening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.openBrewingModal]);

  // Handle tab query param (e.g., ?tab=logs)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'logs') {
      setActiveTab('logs');
    }
  }, [location.search]);

  // Load list of brewed ingredient IDs from brewing recipes
  const loadBrewedIngredientIds = async () => {
    try {
      const response = await axios.get(`${API_URL}/brewing/recipes`);
      const recipes = response.data.data || [];
      const ids = recipes
        .filter(r => r.resultIngredientId)
        .map(r => r.resultIngredientId._id || r.resultIngredientId);
      setBrewedIngredientIds(ids);
    } catch (error) {
      console.error('Error loading brewed ingredient IDs:', error);
    }
  };

  // Load brewing logs
  const loadBrewingLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const params = new URLSearchParams();
      if (logsFilters.startDate) params.append('startDate', logsFilters.startDate);
      if (logsFilters.endDate) params.append('endDate', logsFilters.endDate);
      if (logsFilters.status) params.append('status', logsFilters.status);

      const response = await axios.get(`${API_URL}/brewing/logs?${params.toString()}`);
      if (response.data.success) {
        setBrewingLogs(response.data.data);
      }
    } catch (error) {
      toast.error('Không thể tải lịch sử ủ');
    } finally {
      setLogsLoading(false);
    }
  }, [logsFilters]);

  // Load brewing logs when tab changes or filters change
  useEffect(() => {
    if (activeTab === 'logs') {
      loadBrewingLogs();
    }
  }, [activeTab, loadBrewingLogs]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/ingredients/${id}`);
      setIngredients(prev => prev.filter(i => i._id !== id));
      setShowDeleteConfirm(null);
    } catch (error) {
      toast.error('Không thể xóa nguyên liệu');
    }
  };

  // Brewing functions
  const openBrewingModal = async () => {
    try {
      const response = await axios.get(`${API_URL}/brewing/recipes`);
      setBrewingRecipes(response.data.data || []);
      setSelectedRecipe(null);
      setBrewingForm({ batches: 1, autoDiscardEndOfDay: false, note: '' });
      setShowBrewingModal(true);
    } catch (error) {
      toast.error('Không thể tải danh sách công thức ủ');
    }
  };

  const handleBrew = async () => {
    if (!selectedRecipe) {
      toast.error('Vui lòng chọn công thức');
      return;
    }
    
    try {
      setBrewingLoading(true);
      const response = await axios.post(`${API_URL}/brewing/brew`, {
        recipeId: selectedRecipe._id,
        batches: brewingForm.batches,
        autoDiscardEndOfDay: brewingForm.autoDiscardEndOfDay,
        note: brewingForm.note
      });
      
      toast.success(response.data.message);
      setShowBrewingModal(false);
      loadIngredients(1, true); // Reload from first page
    } catch (error) {
      if (error.response?.data?.insufficientIngredients) {
        const items = error.response.data.insufficientIngredients;
        toast.error(`Không đủ nguyên liệu: ${items.map(i => `${i.name} (cần ${i.needed}, có ${i.available} ${i.unit})`).join(', ')}`);
      } else {
        toast.error(error.response?.data?.message || 'Lỗi khi ủ nguyên liệu');
      }
    } finally {
      setBrewingLoading(false);
    }
  };

  // Delete brewing log
  const handleDeleteBrewingLog = async (id, restoreIngredients = false) => {
    try {
      await axios.delete(`${API_URL}/brewing/logs/${id}?restoreIngredients=${restoreIngredients}`);
      toast.success(restoreIngredients ? 'Đã xóa và hoàn nguyên liệu' : 'Đã xóa lịch sử ủ');
      setDeleteBrewingLogConfirm(null);
      loadBrewingLogs();
      if (restoreIngredients) {
        loadIngredients(1, true);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể xóa');
    }
  };

  // Delete multiple brewing logs
  const handleBulkDeleteBrewingLogs = async (restoreIngredients = false) => {
    if (selectedBrewingLogs.length === 0) return;
    
    try {
      await axios.delete(`${API_URL}/brewing/logs`, {
        data: { ids: selectedBrewingLogs, restoreIngredients }
      });
      toast.success(`Đã xóa ${selectedBrewingLogs.length} lịch sử ủ${restoreIngredients ? ' và hoàn nguyên liệu' : ''}`);
      setDeleteBrewingLogConfirm(null);
      setSelectedBrewingLogs([]);
      loadBrewingLogs();
      if (restoreIngredients) {
        loadIngredients(1, true);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể xóa');
    }
  };

  const toggleBrewingLogSelect = (id) => {
    setSelectedBrewingLogs(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAllBrewingLogs = () => {
    if (selectedBrewingLogs.length === brewingLogs.length) {
      setSelectedBrewingLogs([]);
    } else {
      setSelectedBrewingLogs(brewingLogs.map(l => l._id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    try {
      await Promise.all(selectedIds.map(id => axios.delete(`${API_URL}/ingredients/${id}`)));
      setIngredients(prev => prev.filter(i => !selectedIds.includes(i._id)));
      setSelectedIds([]);
      setShowDeleteConfirm(null);
    } catch (error) {
      toast.error('Không thể xóa nguyên liệu');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredIngredients.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredIngredients.map(i => i._id));
    }
  };

  // Long press for mobile bulk select
  const handleTouchStart = (id) => {
    const timer = setTimeout(() => {
      if (!selectedIds.includes(id)) {
        setSelectedIds([id]);
      }
    }, 500);
    setLongPressTimer(timer);
  };

  const handleTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const isLowStock = (ingredient) => {
    return ingredient.quantity <= (ingredient.minStock || 0);
  };

  // Format stock quantity - add ~ prefix if decimal
  const formatStock = (quantity) => {
    if (quantity === null || quantity === undefined) return '0';
    // Check if it's a decimal (has fractional part)
    if (quantity % 1 !== 0) {
      return '~' + quantity.toFixed(1);
    }
    return quantity.toString();
  };

  const getAmountDisplay = (ingredient) => {
    // For brewing result ingredients, quantity is stored directly in measurementUnit
    if (isBrewingResult(ingredient)) {
      return `${formatStock(ingredient.quantity)} ${measurementLabels[ingredient.measurementUnit] || ingredient.measurementUnit}`;
    }
    // For piece or item or kg items, just show the quantity with unit
    if (ingredient.packagingUnit === 'piece') {
      return `${formatStock(ingredient.quantity)} Quả`;
    }
    if (ingredient.packagingUnit === 'item') {
      return `${formatStock(ingredient.quantity)} Cái`;
    }
    if (ingredient.packagingUnit === 'kg') {
      return `${formatStock(ingredient.quantity)} Kg`;
    }
    const total = ingredient.quantity * ingredient.amountPerPackage;
    return `${formatStock(total)} ${measurementLabels[ingredient.measurementUnit] || ingredient.measurementUnit}`;
  };

  // Check if ingredient is brewed
  const isBrewedIngredient = (ingredient) => {
    return brewedIngredientIds.includes(ingredient._id);
  };

  // No need for complex filtering - backend handles it
  // Just do local search matching for immediate feedback
  const filteredIngredients = ingredients.filter(i => {
    if (!debouncedSearch) return true;
    const searchLower = debouncedSearch.toLowerCase();
    const searchNormalized = removeVietnameseDiacritics(debouncedSearch);
    const nameLower = (i.name || '').toLowerCase();
    const nameNormalized = removeVietnameseDiacritics(i.name);
    return nameLower.includes(searchLower) || nameNormalized.includes(searchNormalized);
  });

  // Helper functions for brewing logs
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(Math.round(amount || 0)) + 'đ';
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('vi-VN');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <FiCheckCircle size={12} />
            Đang dùng
          </span>
        );
      case 'discarded':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <FiXCircle size={12} />
            Đã hủy
          </span>
        );
      case 'used':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
            <FiCheckCircle size={12} />
            Đã dùng hết
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
            {status}
          </span>
        );
    }
  };

  const getUnitLabel = (unit) => {
    const labels = { ml: 'ml', liter: 'lít', g: 'g', kg: 'kg', piece: 'quả', unit: 'cái' };
    return labels[unit] || unit;
  };

  // Calculate stats for brewing logs
  const logsStats = {
    total: brewingLogs.length,
    active: brewingLogs.filter(l => l.status === 'active').length,
    discarded: brewingLogs.filter(l => l.status === 'discarded').length,
    totalCost: brewingLogs.reduce((sum, l) => sum + (l.ingredientCost || 0), 0)
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header with Tabs */}
      <div className="flex flex-col gap-3 mb-4">
        {/* Title and Tabs Row */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FiDroplet className="text-purple-600" />
            Nguyên liệu
          </h2>
          
          {/* Tabs */}
          <div className="flex bg-gray-100 rounded-lg p-0.5 md:p-1">
            <button
              onClick={() => setActiveTab('list')}
              className={`flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1 md:py-1.5 rounded-md text-xs md:text-sm font-medium transition-colors ${
                activeTab === 'list' 
                  ? 'bg-white text-purple-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FiList size={14} />
              <span className="hidden sm:inline">Danh sách</span>
              <span className="sm:hidden">DS</span>
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1 md:py-1.5 rounded-md text-xs md:text-sm font-medium transition-colors ${
                activeTab === 'logs' 
                  ? 'bg-white text-purple-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FiClock size={14} />
              <span className="hidden sm:inline">Lịch sử ủ</span>
              <span className="sm:hidden">Ủ</span>
            </button>
          </div>
        </div>

        {/* Actions - only show for list tab */}
        {activeTab === 'list' && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[120px]">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Tìm nguyên liệu..."
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
              />
            </div>
            <select
              value={ingredientFilter}
              onChange={(e) => setIngredientFilter(e.target.value)}
              className={`px-2 md:px-3 py-2 border rounded-lg text-sm bg-white ${ingredientFilter === 'lowStock' ? 'border-orange-500 text-orange-600' : ''}`}
            >
              <option value="all">Tất cả</option>
              <option value="lowStock">⚠️ Sắp hết</option>
              <option value="imported">Nguyên liệu nhập</option>
              <option value="brewed">Nguyên liệu ủ</option>
            </select>
            <button
              onClick={openBrewingModal}
              className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm"
            >
              <FiCoffee size={16} /> {!isMobile && 'Ủ nguyên liệu'}
            </button>
            <button
              onClick={() => onAdd()}
              className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
            >
              <FiPlus size={16} /> {!isMobile && 'Thêm nguyên liệu'}
            </button>
          </div>
        )}

        {/* Actions for logs tab */}
        {activeTab === 'logs' && (
          <button
            onClick={loadBrewingLogs}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
          >
            <FiRefreshCw size={16} />
            Làm mới
          </button>
        )}
      </div>

      {/* LIST TAB CONTENT */}
      {activeTab === 'list' && (
        <>
          {/* Bulk Action Bar */}
          {selectedIds.length > 0 && (
            <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="font-medium text-purple-700">{selectedIds.length} đã chọn</span>
                <button
                  onClick={() => setSelectedIds([])}
                  className="text-sm text-purple-600 hover:text-purple-800"
                >
                  Bỏ chọn
                </button>
              </div>
              <button
                onClick={() => setShowDeleteConfirm({ bulk: true })}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <FiTrash2 size={16} /> Xóa
              </button>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          ) : filteredIngredients.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
              <FiDroplet size={48} className="mb-2 opacity-50" />
              <p>Chưa có nguyên liệu nào</p>
            </div>
          ) : (
            <div className="flex-1 overflow-auto">
          {/* Desktop Table */}
          {!isMobile ? (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === filteredIngredients.length && filteredIngredients.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tên nguyên liệu</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Đơn vị nhập</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tồn kho</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Định lượng</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tổng</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Trạng thái</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredIngredients.map((ingredient) => (
                    <tr key={ingredient._id} className={`hover:bg-gray-50 ${selectedIds.includes(ingredient._id) ? 'bg-purple-50' : ''}`}>
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(ingredient._id)}
                          onChange={() => toggleSelect(ingredient._id)}
                          className="w-4 h-4 text-purple-600 rounded"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-medium">{ingredient.name}</span>
                        {(ingredient.usageType === 'direct' || (!ingredient.usageType && ingredient.isDirect)) && (
                          <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                            Trực tiếp
                          </span>
                        )}
                        {ingredient.usageType === 'both' && (
                          <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                            Cả hai
                          </span>
                        )}
                        {ingredient.autoDiscardEndOfDay && (
                          <span className="ml-2 px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs" title="Tự động hủy cuối ngày">
                            🗑️ Hủy cuối ngày
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {getDisplayUnit(ingredient)}
                      </td>
                      <td className="px-4 py-4 text-right font-medium">
                        {formatStock(ingredient.quantity)} {getDisplayUnit(ingredient)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {isBrewingResult(ingredient) ? (
                          <div>
                            <span className="text-purple-600 text-xs">Từ công thức ủ</span>
                            {ingredient.recipeUnit && ingredient.recipeUnit !== ingredient.measurementUnit && (
                              <span className="block text-xs text-gray-500">
                                Công thức: {measurementLabels[ingredient.recipeUnit] || ingredient.recipeUnit}
                              </span>
                            )}
                          </div>
                        ) : ['piece', 'item', 'kg'].includes(ingredient.packagingUnit) ? (
                          ingredient.recipeUnit === 'piece' && ingredient.packagingUnit === 'kg' && ingredient.piecesPerPackage ? (
                            <>1 Kg = {ingredient.piecesPerPackage} quả</>
                          ) : '-'
                        ) : (
                          <>
                            1 {packagingLabels[ingredient.packagingUnit]} = {ingredient.amountPerPackage} {measurementLabels[ingredient.measurementUnit]}
                            {ingredient.recipeUnit === 'piece' && ingredient.piecesPerPackage > 1 && (
                              <span className="block text-xs text-yellow-600">({ingredient.piecesPerPackage} quả)</span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right text-sm text-gray-600">
                        {getAmountDisplay(ingredient)}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {isLowStock(ingredient) ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                            <FiAlertTriangle size={12} /> Sắp hết
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                            Còn hàng
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => onEdit(ingredient)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Sửa"
                          >
                            <FiEdit2 size={16} />
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm({ id: ingredient._id, name: ingredient.name })}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Xóa"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Loading more indicator for desktop */}
              {loadingMore && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                </div>
              )}
              
              {/* Load more trigger for desktop */}
              {!loadingMore && hasMore && (
                <div ref={observerTarget} className="h-10" />
              )}
            </div>
          ) : (
            /* Mobile Cards */
            <div className="space-y-2">
              {filteredIngredients.map((ingredient) => (
                <div
                  key={ingredient._id}
                  onTouchStart={() => handleTouchStart(ingredient._id)}
                  onTouchEnd={handleTouchEnd}
                  onTouchMove={handleTouchEnd}
                  className={`bg-white border rounded-xl p-3 ${selectedIds.includes(ingredient._id) ? 'border-purple-500 bg-purple-50' : ''}`}
                  onClick={() => {
                    if (selectedIds.length > 0) {
                      toggleSelect(ingredient._id);
                    }
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {selectedIds.length > 0 && (
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selectedIds.includes(ingredient._id) ? 'bg-purple-500 border-purple-500' : 'border-gray-300'}`}>
                            {selectedIds.includes(ingredient._id) && <FiCheck className="text-white" size={12} />}
                          </div>
                        )}
                        <span className="font-medium">{ingredient.name}</span>
                        {(ingredient.usageType === 'direct' || (!ingredient.usageType && ingredient.isDirect)) && (
                          <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                            Trực tiếp
                          </span>
                        )}
                        {ingredient.usageType === 'both' && (
                          <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                            Cả hai
                          </span>
                        )}
                        {ingredient.autoDiscardEndOfDay && (
                          <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">
                            🗑️ Hủy cuối ngày
                          </span>
                        )}
                        {isLowStock(ingredient) && (
                          <FiAlertTriangle className="text-red-500" size={14} />
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                        <span>
                          Tồn: <strong className={isLowStock(ingredient) ? 'text-red-600' : 'text-green-600'}>
                            {formatStock(ingredient.quantity)} {getDisplayUnit(ingredient)}
                          </strong>
                        </span>
                        {!isBrewingResult(ingredient) && (
                          <span className="text-gray-400">
                            ({getAmountDisplay(ingredient)})
                          </span>
                        )}
                      </div>
                      {!isBrewingResult(ingredient) && !['piece', 'item', 'kg'].includes(ingredient.packagingUnit) && (
                        <div className="text-xs text-gray-400 mt-1">
                          1 {packagingLabels[ingredient.packagingUnit]} = {ingredient.amountPerPackage} {measurementLabels[ingredient.measurementUnit]}
                          {ingredient.recipeUnit === 'piece' && ingredient.piecesPerPackage > 1 && (
                            <span className="text-yellow-600"> ({ingredient.piecesPerPackage} quả)</span>
                          )}
                        </div>
                      )}
                      {isBrewingResult(ingredient) && ingredient.recipeUnit && ingredient.recipeUnit !== ingredient.measurementUnit && (
                        <div className="text-xs text-purple-500 mt-1">
                          Công thức dùng: {measurementLabels[ingredient.recipeUnit] || ingredient.recipeUnit}
                        </div>
                      )}
                      {!isBrewingResult(ingredient) && ingredient.recipeUnit === 'piece' && ingredient.packagingUnit === 'kg' && ingredient.piecesPerPackage && (
                        <div className="text-xs text-yellow-600 mt-1">
                          1 Kg = {ingredient.piecesPerPackage} quả
                        </div>
                      )}
                    </div>
                    {selectedIds.length === 0 && (
                      <div className="flex items-center gap-1 ml-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); onEdit(ingredient); }}
                          className="p-2 text-blue-600"
                        >
                          <FiEdit2 size={18} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm({ id: ingredient._id, name: ingredient.name }); }}
                          className="p-2 text-red-500"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Loading more indicator */}
              {loadingMore && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                </div>
              )}
              
              {/* Load more trigger */}
              {!loadingMore && hasMore && (
                <div ref={observerTarget} className="h-10" />
              )}
            </div>
          )}
        </div>
      )}
        </>
      )}

      {/* BREWING LOGS TAB CONTENT */}
      {activeTab === 'logs' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-white rounded-xl p-3 shadow-sm border">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FiPackage className="text-purple-600" size={16} />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Tổng lần ủ</p>
                  <p className="text-lg font-bold">{logsStats.total}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 shadow-sm border">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FiCheckCircle className="text-green-600" size={16} />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Đang dùng</p>
                  <p className="text-lg font-bold text-green-600">{logsStats.active}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 shadow-sm border">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-red-100 rounded-lg">
                  <FiTrash2 className="text-red-600" size={16} />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Đã hủy</p>
                  <p className="text-lg font-bold text-red-600">{logsStats.discarded}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 shadow-sm border">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <FiDollarSign className="text-yellow-600" size={16} />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Tổng chi phí</p>
                  <p className="text-lg font-bold">{formatCurrency(logsStats.totalCost)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl p-3 shadow-sm border mb-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <FiCalendar className="text-gray-400" size={14} />
                <span className="text-sm text-gray-500">Từ:</span>
                <div className="relative">
                  <input
                    type="date"
                    value={logsFilters.startDate}
                    onChange={(e) => setLogsFilters(prev => ({ ...prev, startDate: e.target.value }))}
                    className="sr-only"
                    id="logsStartDate"
                  />
                  <label
                    htmlFor="logsStartDate"
                    onClick={() => document.getElementById('logsStartDate').showPicker()}
                    className="px-3 py-1.5 border rounded-lg text-sm cursor-pointer hover:bg-gray-50 inline-block min-w-[100px]"
                  >
                    {logsFilters.startDate ? formatDisplayDate(logsFilters.startDate) : 'Chọn ngày'}
                  </label>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Đến:</span>
                <div className="relative">
                  <input
                    type="date"
                    value={logsFilters.endDate}
                    onChange={(e) => setLogsFilters(prev => ({ ...prev, endDate: e.target.value }))}
                    className="sr-only"
                    id="logsEndDate"
                  />
                  <label
                    htmlFor="logsEndDate"
                    onClick={() => document.getElementById('logsEndDate').showPicker()}
                    className="px-3 py-1.5 border rounded-lg text-sm cursor-pointer hover:bg-gray-50 inline-block min-w-[100px]"
                  >
                    {logsFilters.endDate ? formatDisplayDate(logsFilters.endDate) : 'Chọn ngày'}
                  </label>
                </div>
              </div>

              <select
                value={logsFilters.status}
                onChange={(e) => setLogsFilters(prev => ({ ...prev, status: e.target.value }))}
                className="px-3 py-1.5 border rounded-lg text-sm"
              >
                <option value="">Tất cả</option>
                <option value="active">Đang dùng</option>
                <option value="discarded">Đã hủy</option>
                <option value="used">Đã dùng hết</option>
              </select>

              {(logsFilters.startDate || logsFilters.endDate || logsFilters.status) && (
                <button
                  onClick={() => setLogsFilters({ startDate: '', endDate: '', status: '' })}
                  className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                >
                  Xóa lọc
                </button>
              )}
            </div>
          </div>

          {/* Logs List */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden flex-1">
            {logsLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : brewingLogs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <FiClock size={48} className="mb-4 text-gray-300" />
                <p>Chưa có lịch sử ủ nguyên liệu</p>
              </div>
            ) : (
              <>
                {/* Bulk actions bar */}
                {selectedBrewingLogs.length > 0 && (
                  <div className="flex items-center justify-between p-3 bg-purple-50 border-b">
                    <span className="text-sm text-purple-700">
                      Đã chọn {selectedBrewingLogs.length} mục
                    </span>
                    <button
                      onClick={() => setDeleteBrewingLogConfirm({ bulk: true })}
                      className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                    >
                      <FiTrash2 size={14} />
                      Xóa đã chọn
                    </button>
                  </div>
                )}
                
                {/* Desktop Table */}
                {!isMobile ? (
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-3 text-center w-10">
                          <input
                            type="checkbox"
                            checked={selectedBrewingLogs.length === brewingLogs.length && brewingLogs.length > 0}
                            onChange={toggleSelectAllBrewingLogs}
                            className="rounded text-purple-600"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thời gian</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Công thức</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thành phẩm</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Số mẻ</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sản lượng</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Chi phí</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Trạng thái</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-16"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {brewingLogs.map(log => (
                        <tr key={log._id} className={`hover:bg-gray-50 ${selectedBrewingLogs.includes(log._id) ? 'bg-purple-50' : ''}`}>
                          <td className="px-3 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedBrewingLogs.includes(log._id)}
                              onChange={() => toggleBrewingLogSelect(log._id)}
                              className="rounded text-purple-600"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <FiClock className="text-gray-400" size={14} />
                              <span className="text-sm">{formatDate(log.brewedAt)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-medium text-purple-600">
                              {log.recipe?.name || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm">
                              {log.outputIngredient?.name || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 bg-purple-100 text-purple-700 rounded-full font-medium text-sm">
                              {log.batches}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-medium">
                              {log.totalOutput} {getUnitLabel(log.outputUnit)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-medium text-yellow-600">
                              {formatCurrency(log.ingredientCost)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {getStatusBadge(log.status)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => setDeleteBrewingLogConfirm({ 
                                id: log._id, 
                                name: log.recipe?.name || 'N/A',
                                output: `${log.totalOutput} ${getUnitLabel(log.outputUnit)}`,
                                status: log.status
                              })}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Xóa"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  /* Mobile Cards */
                  <div className="divide-y">
                    {brewingLogs.map(log => (
                      <div key={log._id} className={`p-4 ${selectedBrewingLogs.includes(log._id) ? 'bg-purple-50' : ''}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={selectedBrewingLogs.includes(log._id)}
                              onChange={() => toggleBrewingLogSelect(log._id)}
                              className="mt-1 rounded text-purple-600"
                            />
                            <div>
                              <h3 className="font-medium text-purple-600">{log.recipe?.name || 'N/A'}</h3>
                              <p className="text-sm text-gray-500">{log.outputIngredient?.name || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(log.status)}
                            <button
                              onClick={() => setDeleteBrewingLogConfirm({ 
                                id: log._id, 
                                name: log.recipe?.name || 'N/A',
                                output: `${log.totalOutput} ${getUnitLabel(log.outputUnit)}`,
                                status: log.status
                              })}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-sm mb-2 ml-6">
                          <div className="flex items-center gap-1 text-gray-500">
                            <FiClock size={14} />
                            {formatDate(log.brewedAt)}
                          </div>
                          <div className="text-right font-medium">
                            {log.batches} mẻ × {log.outputPerBatch} {getUnitLabel(log.outputUnit)}
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between text-sm ml-6">
                          <span className="text-gray-500">
                            Tổng: <strong>{log.totalOutput} {getUnitLabel(log.outputUnit)}</strong>
                          </span>
                          <span className="text-yellow-600 font-medium">
                            {formatCurrency(log.ingredientCost)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Delete Brewing Log Confirm Modal */}
      {deleteBrewingLogConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-4">
            <h3 className="font-semibold mb-2 text-lg">
              {deleteBrewingLogConfirm.bulk 
                ? `Xóa ${selectedBrewingLogs.length} lịch sử ủ?` 
                : `Xóa lịch sử ủ "${deleteBrewingLogConfirm.name}"?`}
            </h3>
            
            {!deleteBrewingLogConfirm.bulk && (
              <p className="text-sm text-gray-600 mb-3">
                Sản lượng: <strong>{deleteBrewingLogConfirm.output}</strong>
              </p>
            )}
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800 mb-2">
                <strong>Lưu ý:</strong> Bạn có muốn hoàn lại nguyên liệu không?
              </p>
              <ul className="text-xs text-yellow-700 list-disc list-inside space-y-1">
                <li><strong>Hoàn nguyên liệu:</strong> Trả lại nguyên liệu đã dùng, trừ thành phẩm đã tạo</li>
                <li><strong>Chỉ xóa:</strong> Chỉ xóa lịch sử, không thay đổi tồn kho</li>
              </ul>
            </div>
            
            <div className="flex flex-col gap-2">
              <button
                onClick={() => deleteBrewingLogConfirm.bulk 
                  ? handleBulkDeleteBrewingLogs(true) 
                  : handleDeleteBrewingLog(deleteBrewingLogConfirm.id, true)}
                className="w-full py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
              >
                Xóa và hoàn nguyên liệu
              </button>
              <button
                onClick={() => deleteBrewingLogConfirm.bulk 
                  ? handleBulkDeleteBrewingLogs(false) 
                  : handleDeleteBrewingLog(deleteBrewingLogConfirm.id, false)}
                className="w-full py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                Chỉ xóa lịch sử
              </button>
              <button
                onClick={() => setDeleteBrewingLogConfirm(null)}
                className="w-full py-2.5 border rounded-lg hover:bg-gray-50"
              >
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-4">
            <h3 className="font-semibold mb-2">
              {showDeleteConfirm.bulk 
                ? `Xóa ${selectedIds.length} nguyên liệu?` 
                : `Xóa "${showDeleteConfirm.name}"?`}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Hành động này không thể hoàn tác.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={() => showDeleteConfirm.bulk ? handleBulkDelete() : handleDelete(showDeleteConfirm.id)}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brewing Modal */}
      {showBrewingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex justify-between items-center flex-shrink-0">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <FiCoffee className="text-amber-500" />
                Ủ nguyên liệu
              </h2>
              <button onClick={() => setShowBrewingModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <FiX size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              {/* Recipe Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Chọn công thức ủ *</label>
                {brewingRecipes.length > 0 ? (
                  <div className="space-y-1.5 max-h-[280px] overflow-y-auto border rounded-lg p-2">
                    {brewingRecipes.map(recipe => (
                      <div 
                        key={recipe._id}
                        onClick={() => setSelectedRecipe(recipe)}
                        className={`px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
                          selectedRecipe?._id === recipe._id 
                            ? 'bg-amber-50 border-amber-500' 
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{recipe.name}</span>
                          {selectedRecipe?._id === recipe._id && (
                            <FiCheck className="text-amber-600" size={16} />
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Thành phẩm: {recipe.outputAmount} {recipe.outputUnit}
                          {recipe.resultIngredientId && ` → ${recipe.resultIngredientId.name}`}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
                    <p>Chưa có công thức ủ nào</p>
                    <p className="text-sm mt-1">Vui lòng tạo công thức loại "Ủ" trong Quản lý công thức</p>
                  </div>
                )}
              </div>

              {/* Batches */}
              {selectedRecipe && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Số lần ủ (mẻ) *
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={brewingForm.batches}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d+$/.test(val)) {
                          setBrewingForm(prev => ({ ...prev, batches: val === '' ? '' : parseInt(val) }));
                        }
                      }}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value) || 1;
                        setBrewingForm(prev => ({ ...prev, batches: Math.max(1, val) }));
                      }}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Sẽ tạo ra: <span className="font-medium text-amber-600">
                        {selectedRecipe.outputAmount * (brewingForm.batches || 1)} {selectedRecipe.outputUnit}
                      </span>
                    </p>
                  </div>

                  {/* Ingredients needed */}
                  <div className="p-2.5 bg-gray-50 rounded-lg">
                    <p className="font-medium text-xs text-gray-600 mb-1.5">Nguyên liệu cần dùng:</p>
                    <div className="space-y-0.5 text-sm">
                      {selectedRecipe.ingredients?.map((ing, idx) => (
                        <div key={idx} className="flex justify-between">
                          <span className="text-gray-700">{ing.ingredientId?.name || ing.ingredientName || 'N/A'}</span>
                          <span className="text-gray-500 font-medium">
                            {(ing.amount * (brewingForm.batches || 1)).toFixed(2)} {ing.unit}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Auto discard checkbox */}
                  <label className="flex items-start gap-2.5 p-2.5 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={brewingForm.autoDiscardEndOfDay}
                      onChange={(e) => setBrewingForm(prev => ({ ...prev, autoDiscardEndOfDay: e.target.checked }))}
                      className="mt-0.5 w-4 h-4 text-amber-600 rounded"
                    />
                    <div>
                      <span className="font-medium text-sm">Hủy lượng thừa cuối ngày</span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Tồn kho còn lại sẽ tự động bị hủy vào cuối ngày
                      </p>
                    </div>
                  </label>

                  {/* Note */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Ghi chú</label>
                    <textarea
                      value={brewingForm.note}
                      onChange={(e) => setBrewingForm(prev => ({ ...prev, note: e.target.value }))}
                      rows={2}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                      placeholder="Ghi chú (không bắt buộc)"
                    />
                  </div>
                </>
              )}
            </div>
            
            <div className="flex gap-3 p-4 border-t bg-white flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowBrewingModal(false)}
                className="flex-1 py-2.5 border rounded-lg hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleBrew}
                disabled={!selectedRecipe || brewingLoading}
                className="flex-1 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {brewingLoading ? 'Đang xử lý...' : 'Ủ nguyên liệu'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IngredientsListView;
